<template>
    <div class="row exports-category">
        <div class="col-xs-12">
            <div class="box box-primary">
                <div class="box-header with-border">
                    <div class="box-header__inner">
                        <h3 class="box-title">{{ category.name }}</h3>

                        <button
                            v-if="category.description"
                            type="button"
                            class="btn btn-sm btn-primary btn-icon btn-round ml5"
                            data-toggle="popover"
                            data-trigger="focus"
                            data-placement="top"
                            :data-html="true"
                            :data-content="category.description"
                        >
                            <i class="fa fa-info" />
                        </button>

                        <div class="box-tools pull-right">
                            <template v-if="isSingleExport">
                                <label
                                    v-if="exportsData[0].type === EXPORT_TYPE_FILE"
                                    class="btn btn-sm btn-primary mr10"
                                    :title="$t('export.newExport')"
                                    :disabled="isUploading"
                                >
                                    {{ $t('export.newExport') }}

                                    <input
                                        ref="file"
                                        type="file"
                                        accept="text/csv,text/csv,text/comma-separated-values,application/octet-stream,application/txt"
                                        :disabled="isUploading || isCreating"
                                        hidden
                                        @change="createProcess({ exportData: exportsData[0], event: $event })"
                                    />
                                </label>

                                <button
                                    v-if="exportsData[0].type === EXPORT_TYPE_REGULAR"
                                    type="button"
                                    class="btn btn-sm btn-primary mr10"
                                    :title="$t('export.newExport')"
                                    :disabled="isLoading"
                                    @click="createProcess({ exportData: exportsData[0] })"
                                >
                                    {{ $t('export.newExport') }}
                                </button>

                                <button
                                    v-if="exportsData[0].type === EXPORT_TYPE_ENTITY"
                                    :title="$t('export.newExport')"
                                    class="btn btn-sm btn-primary mr10"
                                    data-target="#exports-modal"
                                    data-toggle="modal"
                                    type="button"
                                    @click="$emit('showExports', category)"
                                >
                                    {{ $t('export.newExport') }}
                                </button>
                            </template>

                            <button
                                v-else
                                type="button"
                                class="btn btn-sm btn-primary mr10"
                                :title="$t('export.newExport')"
                                data-toggle="modal"
                                data-target="#exports-modal"
                                @click="$emit('showExports', category)"
                            >
                                {{ $t('export.newExport') }}
                            </button>

                            <button
                                class="btn btn-box-tool"
                                :title="$t('collapse')"
                                type="button"
                                @click="isCollapseActive = !isCollapseActive"
                            >
                                <i
                                    class="fa"
                                    :class="{
                                        'fa-plus': !isCollapseActive,
                                        'fa-minus': isCollapseActive,
                                    }"
                                />
                            </button>
                        </div>
                    </div>

                    <vue-slide-up-down
                        class="exports-progress"
                        :active="isUploading"
                    >
                        <div
                            class="progress"
                            :class="{
                                'progress-primary': uploadingProgressValue < 100,
                                'progress-success': uploadingProgressValue >= 100,
                            }"
                        >
                            <div
                                class="progress-bar progress-bar-striped active"
                                :class="{
                                    'progress-bar-primary': uploadingProgressValue < 100,
                                    'progress-bar-success': uploadingProgressValue >= 100,
                                }"
                                role="progressbar"
                                :aria-valuenow="uploadingProgressValue"
                                aria-valuemin="0"
                                aria-valuemax="100"
                                :style="{ width: `${uploadingProgressValue}%` }"
                            >
                                <div class="progress-bar-value">{{ uploadingProgressText }}</div>
                            </div>
                        </div>
                    </vue-slide-up-down>
                </div>

                <vue-slide-up-down :active="isCollapseActive">
                    <div
                        v-if="isCreating || processesData.length"
                        class="box-body"
                    >
                        <table class="table">
                            <thead>
                                <tr>
                                    <th width="40px">{{ $t('id') }}</th>

                                    <th width="120px">{{ $t('status') }}</th>

                                    <th width="340px">
                                        {{ $t('name') }}
                                    </th>

                                    <th width="140px">{{ $t('author') }}</th>

                                    <th width="200px">{{ $t('startAt') }}</th>

                                    <th width="auto">{{ $t('endAt') }}</th>

                                    <th width="160px"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-if="isCreating"
                                    class="exports-process is-show"
                                >
                                    <td colspan="8">
                                        <img
                                            class="table-row-preloader"
                                            rel="preload"
                                            src="/img/table-row-preloader.gif"
                                        />
                                    </td>
                                </tr>

                                <tr
                                    v-for="process in processesData"
                                    :key="process.id"
                                    class="exports-process"
                                    :class="{ 'is-show': process.isShow }"
                                >
                                    <td>
                                        <div>{{ process.id }}</div>
                                    </td>

                                    <td>
                                        <div>
                                            <button
                                                v-if="process.status === PROCESS_STATUS_ERROR"
                                                type="button"
                                                class="btn btn-sm btn-danger label exports-process-label"
                                                data-toggle="popover"
                                                data-trigger="hover"
                                                data-placement="bottom"
                                                :data-html="true"
                                                :data-content="process.error ? `<b>${$t('error')}:</b> ${process.error}` : undefined"
                                            >
                                                {{ process.statusTitle }}
                                            </button>

                                            <span
                                                v-else
                                                class="label exports-process-label"
                                                :class="{
                                                    'bg-primary': process.status === PROCESS_STATUS_PROCESSING,
                                                    'bg-yellow': process.status === PROCESS_STATUS_CANCELED,
                                                    'bg-green': process.status === PROCESS_STATUS_FINISHED,
                                                }"
                                                :title="process.statusTitle"
                                            >
                                                {{ process.statusTitle }}
                                            </span>
                                        </div>
                                    </td>

                                    <td>
                                        <div>
                                            <span>
                                                {{ process.name }}
                                                <template v-if="process.route">
                                                    (<a :href=process.route :title="process.extraName">{{ process.extraName }}</a>)
                                                </template>
                                            </span>
                                        </div>
                                    </td>

                                    <td>
                                        <div>{{ process.user ? process.user.name : $t('none') }}</div>
                                    </td>

                                    <td>
                                        <div>{{ formatDate(process.createdAt) }}</div>
                                    </td>

                                    <td>
                                        <div>
                                            <div
                                                v-if="process.status === PROCESS_STATUS_PROCESSING"
                                                class="progress progress-primary"
                                            >
                                                <div
                                                    class="progress-bar progress-bar-primary progress-bar-striped active"
                                                    role="progressbar"
                                                    :aria-valuenow="process.progressValue"
                                                    aria-valuemin="0"
                                                    aria-valuemax="100"
                                                    :style="{ width: `${process.progressValue}%` }"
                                                >
                                                    <div class="progress-bar-value">{{ `${process.progressValue}%` }}</div>
                                                </div>
                                            </div>

                                            <span v-else>{{ formatDate(process.endAt) }}</span>
                                        </div>
                                    </td>

                                    <td class="text-right text-nowrap">
                                        <div>
                                            <button
                                                v-if="process.status === PROCESS_STATUS_CANCELED"
                                                type="button"
                                                class="btn btn-sm btn-warning"
                                                :title="$t('start')"
                                                :disabled="isLoading"
                                                @click="$emit('startProcess', { ...process, category })"
                                            >
                                                <i class="fa fa-play" />
                                                <span class="sr-only">
                                                    {{ $t('start') }}
                                                </span>
                                            </button>

                                            <a
                                                v-if="process.status === PROCESS_STATUS_FINISHED && process.filename"
                                                :href="`/additional/exports/download/export-file/${process.id}`"
                                                class="btn btn-sm btn-success"
                                                :title="$t('download')"
                                            >
                                                <i class="fa fa-download" />
                                                <span class="sr-only">{{ $t('download') }}</span>
                                            </a>

                                            <button
                                                v-if="process.status === PROCESS_STATUS_PROCESSING"
                                                type="button"
                                                class="btn btn-sm btn-danger"
                                                :title="$t('cancel')"
                                                data-toggle="modal"
                                                data-target="#confirm-modal"
                                                @click="$emit('confirmProcess', { ...process, category })"
                                            >
                                                <i class="fa fa-ban" />
                                                <span class="sr-only">
                                                    {{ $t('cancel') }}
                                                </span>
                                            </button>

                                            <button
                                                v-if="isDeleteEnable(process)"
                                                type="button"
                                                class="btn btn-sm btn-danger"
                                                :title="$t('delete')"
                                                data-toggle="modal"
                                                data-target="#confirm-modal"
                                                @click="$emit('confirmProcess', { ...process, category })"
                                            >
                                                <i class="fa fa-trash" />
                                                <span class="sr-only">
                                                    {{ $t('delete') }}
                                                </span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div
                        v-else
                        class="box-body with-border clearfix"
                    >
                        <p class="mb0">
                            {{ $t('messages.export.noProcesses') }}
                        </p>
                    </div>
                </vue-slide-up-down>
            </div>
        </div>
    </div>
</template>

<script>
import formatsMixin from '@/mixins/formatsMixin';
import exportMixin from '@/mixins/exportMixin';

export default {
    name: 'AdditionalExportsCategory',
    mixins: [formatsMixin, exportMixin],
    props: {
        category: {
            type: Object,
            default: () => {},
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isCollapseActive: true,
        };
    },
    computed: {
        isCreating() {
            return !!this.category.isCreating;
        },
        isUploading() {
            return !!this.category.isUploading;
        },
        uploadingProgressValue() {
            return this.category.uploadingProgressValue || 0;
        },
        uploadingProgressText() {
            return this.category.uploadingProgressText || '';
        },
        exportsData() {
            return [...this.category.exports];
        },
        processesData() {
            return this.category.processes.map((process) => {
                const exportData = this.exportsData.find((e) => e.id === process.exportId);

                if (exportData) {
                    process.type = exportData.type;
                }

                if (process.currentCount === 0 && process.totalCount === 0) {
                    process.progressValue = 0.0;
                } else {
                    process.progressValue = ((process.currentCount / process.totalCount) * 100).toFixed(2);
                }

                switch (process.status) {
                    case this.PROCESS_STATUS_PROCESSING:
                        process.statusTitle = this.$t('processing');
                        break;
                    case this.PROCESS_STATUS_CANCELED:
                        process.statusTitle = this.$t('canceled');
                        break;
                    case this.PROCESS_STATUS_FINISHED:
                        process.statusTitle = this.$t('finished');
                        break;
                    case this.PROCESS_STATUS_ERROR:
                        process.statusTitle = this.$t('error');
                        break;
                }

                return process;
            });
        },
        isSingleExport() {
            return this.exportsData.length <= 1;
        },
        isTypeRegular() {
            return !!this.exportsData.find((exportData) => exportData.type === this.EXPORT_TYPE_REGULAR);
        },
        isTypeFile() {
            return !!this.exportsData.find((exportData) => exportData.type === this.EXPORT_TYPE_FILE);
        },
    },
    watch: {
        isUploading(value) {
            if (value) {
                this.isCollapseActive = true;
            }
        },
        isCreating(value) {
            if (value) {
                this.isCollapseActive = true;
            }
        },
        processes: {
            handler(processes) {
                processes.forEach((process) => {
                    if (process.error) {
                        $('[data-toggle="popover"]').popover();
                    }
                });
            },
            deep: true,
        },
    },
    mounted() {
        $('[data-toggle="popover"]').popover();
    },
    methods: {
        async createProcess({ exportData, event }) {
            await this.$emit('createProcess', { exportData, event });

            if (event) {
                event.target.value = '';
            }
        },
        isDeleteEnable(process) {
            return !!(
                process.status === this.PROCESS_STATUS_FINISHED ||
                process.status === this.PROCESS_STATUS_CANCELED ||
                process.status === this.PROCESS_STATUS_ERROR
            );
        },
    },
};
</script>

<style src="./AdditionalExportsCategory.css" />
